import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import HeaderButtons from "@/components/Header/Asset";
import BaseHeader from "@/components/Header/BaseHeader";
import AssetsTable from "@/components/ListTable/AssetsTable";
import InterventionsTable from "@/components/ListTable/InterventionsTable";
import ObservationsTable from "@/components/ListTable/ObservationsTable";
import { STORAGE_KEYS } from "@/constants/StorageKeys";
import { TAB_KEYS } from "@/constants/TabKeys";
import { useGetAsset } from "@/entities/asset";
import { ASSETS_FILTERS } from "@/entities/assets";
import { INTERVENTIONS_FILTERS } from "@/entities/interventions";
import { OBSERVATIONS_FILTERS } from "@/entities/observations";
import { AppLayoutPage } from "@/modules/app-layout/app-layout-page";
import { AppSaveBar } from "@/modules/app-save-bar";
import AssetAddButton from "@/modules/asset/asset-add-button";
import AssetBreadcrumb from "@/modules/asset/asset-breadcrumb";
import { AssetIndicators } from "@/modules/asset/asset-indicators";
import { AssetPageMap } from "@/modules/asset/asset-page";
import {
  ASSET_TECHNICAL_UPDATE_SCOPE,
  AssetTechnicalUpdate,
} from "@/modules/asset/asset-technical-update";
import { AssetTimeline } from "@/modules/asset/asset-timeline";
import InterventionAddButton from "@/modules/intervention/intervention-add-button";
import { ListProvider } from "@/modules/list";
import { PageItemContextGate } from "@/modules/page-item-context-gate";
import { useIdParam } from "@/services/router";
import { TabView } from "@/ui/tab-view";

const Details = () => {
  const { t } = useTranslation();
  const id = useIdParam();
  const location = useLocation();

  const defaultTab = location.pathname.includes(TAB_KEYS.ASSETS_OBSERVATIONS)
    ? TAB_KEYS.ASSETS_OBSERVATIONS
    : TAB_KEYS.ASSETS_TECHNICAL;

  const { isLoading, asset = {}, context, error } = useGetAsset(id);

  const tabs = [
    {
      key: TAB_KEYS.ASSETS_TECHNICAL,
      label: t("asset:technical.label"),
      children: <AssetTechnicalUpdate asset={asset} />,
    },
    {
      key: TAB_KEYS.ASSETS_COMPONENTS,
      label: t("asset:components.label"),
      children: (
        <ListProvider
          endpoint="assets"
          storageKey={STORAGE_KEYS.ASSETS_COMPONENTS}
          contextTabValue={TAB_KEYS.ASSETS_COMPONENTS}
          hiddenFilters={{ [ASSETS_FILTERS.ASSET_ID]: id }}
        >
          <AssetsTable
            extraActions={
              <AssetAddButton
                ancestors={asset.ancestors}
                defaultCoordinate={asset.coordinate}
                shortButtonText
                sidePanelTitle={t("asset:form.add.component")}
                loading={isLoading}
              />
            }
          />
        </ListProvider>
      ),
    },
    {
      key: TAB_KEYS.ASSETS_INTERVENTIONS,
      label: t("asset:interventions.label"),
      children: (
        <ListProvider
          endpoint="interventions"
          storageKey={STORAGE_KEYS.ASSETS_INTERVENTIONS}
          contextTabValue={TAB_KEYS.ASSETS_INTERVENTIONS}
          hiddenFilters={{ [INTERVENTIONS_FILTERS.ASSET_ID]: id }}
        >
          <InterventionsTable
            extraActions={
              <InterventionAddButton
                ancestors={asset.ancestors}
                shortButtonText
                loading={isLoading}
              />
            }
          />
        </ListProvider>
      ),
    },
    {
      key: TAB_KEYS.ASSETS_OBSERVATIONS,
      label: t("asset:observations.label"),
      children: (
        <ListProvider
          endpoint="observations"
          storageKey={STORAGE_KEYS.ASSETS_OBSERVATIONS}
          contextTabValue={TAB_KEYS.ASSETS_OBSERVATIONS}
          hiddenFilters={{
            [OBSERVATIONS_FILTERS.CATEGORY]: "defect",
            [OBSERVATIONS_FILTERS.ASSET_ID]: id,
          }}
          defaultSort={["-risk_level", "state"]}
        >
          <ObservationsTable
            hiddenColumns={["asset.id", "asset.asset_type_id"]}
          />
        </ListProvider>
      ),
    },
    {
      key: TAB_KEYS.ASSETS_TIMELINE,
      label: t("asset:timeline.label"),
      children: <AssetTimeline assetId={id} />,
    },
  ];

  return (
    <PageItemContextGate model="asset" itemContext={context} queryError={error}>
      <div className="base-header-wrapper">
        <AssetPageMap asset={asset} isLoading={isLoading} />
        <BaseHeader
          title={asset?.name}
          afterTitle={<AssetIndicators asset={asset} />}
          breadcrumb={<AssetBreadcrumb asset={asset} />}
          loading={isLoading}
          headerClass="header--absolute"
          extra={
            !isLoading && [<HeaderButtons key="HeaderButtons" asset={asset} />]
          }
        />
      </div>
      <AppLayoutPage>
        <TabView
          isNav
          tabs={tabs}
          alwaysSyncSearchParams // keep it to make sure AppSaveBar is always displayed when technical tab is active
          defaultActiveKey={defaultTab}
          tabBarExtraContent={
            <AppSaveBar
              scope={ASSET_TECHNICAL_UPDATE_SCOPE}
              tabs={[TAB_KEYS.ASSETS_TECHNICAL]}
              date={asset?.updated_at}
              loading={isLoading}
            />
          }
        />
      </AppLayoutPage>
    </PageItemContextGate>
  );
};

export default Details;
